// Utility functions for managing draft messages in localStorage

const DRAFT_MESSAGES_KEY = 'chat_draft_messages';

export interface DraftMessage {
  conversationId: number;
  recipientId: number;
  content: string;
  timestamp: number;
  hasImage?: boolean;
}

// Event name to notify listeners that drafts have changed
export const DRAFTS_EVENT_NAME = 'chat:draft-changed';

const emitDraftsChanged = () => {
  try {
    // Only emit in browser environments
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(DRAFTS_EVENT_NAME));
    }
  } catch (e) {
    // no-op
  }
};

// Get all draft messages
export const getAllDraftMessages = (): Record<number, DraftMessage> => {
  try {
    const drafts = localStorage.getItem(DRAFT_MESSAGES_KEY);
    return drafts ? JSON.parse(drafts) : {};
  } catch (error) {
    console.error('Error reading draft messages:', error);
    return {};
  }
};

// Get draft message for a specific conversation
export const getDraftMessage = (conversationId: number): DraftMessage | null => {
  const drafts = getAllDraftMessages();
  return drafts[conversationId] || null;
};

// Save draft message
export const saveDraftMessage = (draft: DraftMessage): void => {
  try {
    const drafts = getAllDraftMessages();
    drafts[draft.conversationId] = draft;
    localStorage.setItem(DRAFT_MESSAGES_KEY, JSON.stringify(drafts));
  emitDraftsChanged();
  } catch (error) {
    console.error('Error saving draft message:', error);
  }
};

// Remove draft message
export const removeDraftMessage = (conversationId: number): void => {
  try {
    const drafts = getAllDraftMessages();
    delete drafts[conversationId];
    localStorage.setItem(DRAFT_MESSAGES_KEY, JSON.stringify(drafts));
  emitDraftsChanged();
  } catch (error) {
    console.error('Error removing draft message:', error);
  }
};

// Clean up old drafts (older than 7 days)
export const cleanupOldDrafts = (): void => {
  try {
    const drafts = getAllDraftMessages();
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    const filteredDrafts = Object.entries(drafts).reduce((acc, [id, draft]) => {
      if (draft.timestamp > sevenDaysAgo) {
        acc[Number(id)] = draft;
      }
      return acc;
    }, {} as Record<number, DraftMessage>);
    
    localStorage.setItem(DRAFT_MESSAGES_KEY, JSON.stringify(filteredDrafts));
  emitDraftsChanged();
  } catch (error) {
    console.error('Error cleaning up draft messages:', error);
  }
};